import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';

// Layout Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import { 
  HomePage,
  ServicesPage,
  // EnrollmentPage, // Hidden for now
  AdmissionPage,
  FormsPage,
  StaffPage,
  ContactPage,
  LoginPage
} from './pages';

// Admin Pages
import { AdminDashboard, AdminLogin } from './admin';

// Form Components
import ExitInterviewForm from './components/forms/ExitInterviewForm';
import HedExitInterviewForm from './components/forms/HedExitInterviewForm';
import StudentNeedsAssessmentForm from './components/forms/StudentNeedsAssessmentForm';
import IntakeInterviewForm from './components/forms/IntakeInterviewForm';
import CounselingReferralForm from './components/forms/CounselingReferralForm';
import SeniorHighExitForm from './components/forms/SeniorHighExitForm';
import FacultyEvaluationForm from './components/forms/FacultyEvaluationForm';
import ServicesEvaluationForm from './components/forms/ServicesEvaluationForm';
import TeacherEvaluationPage from './pages/TeacherEvaluationPage';
import StudentRegistrationPage from './pages/StudentRegistrationPage';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider>
        <AuthProvider>
          <Router>
            <div className="min-h-screen bg-gray-50 flex flex-col">
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
              
              <Routes>
                {/* Admin Routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/*" element={<AdminDashboard />} />
                
                {/* Public Routes with Header/Footer */}
                <Route path="/*" element={
                  <>
                    <Header />
                    <main className="flex-grow">
                      <Routes>
                        {/* Main Pages */}
                        <Route path="/" element={<HomePage />} />
                        <Route path="/services" element={<ServicesPage />} />
                        {/* <Route path="/enrollment" element={<EnrollmentPage />} /> */}
                        <Route path="/admission" element={<AdmissionPage />} />
                        <Route path="/forms" element={<FormsPage />} />
                        <Route path="/staff" element={<StaffPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        
                        {/* Individual Forms */}
                        <Route path="/forms/exit-interview" element={<ExitInterviewForm />} />
                        <Route path="/forms/hed-exit-interview" element={<HedExitInterviewForm />} />
                        <Route path="/forms/student-needs-assessment" element={<StudentNeedsAssessmentForm />} />
                        <Route path="/forms/intake-interview" element={<IntakeInterviewForm />} />
                        <Route path="/forms/counseling-referral" element={<CounselingReferralForm />} />
                        <Route path="/forms/senior-high-exit" element={<SeniorHighExitForm />} />
                        <Route path="/forms/faculty-evaluation" element={<FacultyEvaluationForm />} />
                        <Route path="/forms/services-evaluation" element={<ServicesEvaluationForm />} />
                        
                        {/* Teacher Evaluation */}
                        <Route path="/teacher-evaluation" element={<TeacherEvaluationPage />} />
                        <Route path="/evaluation-register" element={<StudentRegistrationPage />} />
                        
                        {/* 404 Page */}
                        <Route path="*" element={
                          <div className="container mx-auto px-4 py-16 text-center">
                            <h1 className="text-4xl font-bold text-gray-800 mb-4">Page Not Found</h1>
                            <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
                            <a href="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                              Return Home
                            </a>
                          </div>
                        } />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                } />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;